import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectServicePlanWorkflowState,
  selectCurrentWorkflowDefinition,
  updateSingleField,
} from '../../../../redux/slices/servicePlanWorkflowSlice/servicePlanWorkflowSlice';
import {
  selectContactInfo,
  selectHasValidPromoEngagementDiscount,
  selectServicePlan,
} from '../../../../redux/slices/billingSlice/billingSlice';
import { SERVICE_PLAN_PAYMENT_PERIOD } from '../../../../utility/types/ServicePlanWorkflowTypes';
import ServiceAddressForm from './ServiceAddressForm';
import ServiceAddressModal from '../modals/ServiceAddressModal';

const ServicePlanSelectionForm = ({ onChangePlanClick, countryList }) => {
  const dispatch = useDispatch();
  const [showAddressModal, setShowAddressModal] = useState(false);

  const { selectedServicePlan, serviceSelection } = useSelector(selectServicePlanWorkflowState);
  const workflowDef = useSelector(selectCurrentWorkflowDefinition);
  const contactInfo = useSelector(selectContactInfo);
  const activeServicePlan = useSelector(selectServicePlan);
  const hasValidPromoEngagementDiscount = useSelector(selectHasValidPromoEngagementDiscount);

  const { selectedBillingCycle, additionalYears: selectedAdditionalYears } = serviceSelection;
  const { allowChangePlan: showChangePlanLink, billMeReadOnly, showNone } = workflowDef.options;
  const hideZeroAdditionalYears = !showNone;

  const {
    name: servicePlanName,
    monthlyPrice,
    monthlyCrmProductId,
    yearlyPrice,
  } = selectedServicePlan || {};

  const yearly = parseFloat(yearlyPrice);

  let hasYearlyDiscount = false;
  let discountPct = 0;
  let yearlyDiscountedPrice = yearly;
  const isYearlySelected = selectedBillingCycle === SERVICE_PLAN_PAYMENT_PERIOD.YEARLY;
  if (hasValidPromoEngagementDiscount) {
    const promo = activeServicePlan.promotionalEngagement;
    const rawDiscount = Number.isFinite(parseFloat(promo.discount))
      ? parseFloat(promo.discount)
      : 0;
    hasYearlyDiscount = rawDiscount > 0 && rawDiscount < 1;
    if (hasYearlyDiscount) {
      discountPct = Math.round(rawDiscount * 100);
      yearlyDiscountedPrice = Math.floor(yearly * (1 - rawDiscount) * 100) / 100;
    }
  }
  const displayYearlyPrice = hasYearlyDiscount ? yearlyDiscountedPrice : yearly;

  const monthlyAvailable = Boolean(monthlyCrmProductId) && parseFloat(monthlyPrice) >= 1;

  useEffect(() => {
    if (!selectedServicePlan) return;
    if (!monthlyAvailable && selectedBillingCycle === SERVICE_PLAN_PAYMENT_PERIOD.MONTHLY) {
      dispatch(updateSingleField({
        field: 'serviceSelection.selectedBillingCycle',
        value: SERVICE_PLAN_PAYMENT_PERIOD.YEARLY,
      }));
    }
  }, [dispatch, selectedServicePlan, monthlyAvailable, selectedBillingCycle]);

  const additionalYearsOptions = hideZeroAdditionalYears ? [1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5];
  const formatAdditionalYearsLabel = (value) => (value === 0 ? 'None' : `${value} ${value === 1 ? 'Year' : 'Years'}`);

  const handleBillingCycleChange = (v) => {
    dispatch(updateSingleField({ field: 'serviceSelection.selectedBillingCycle', value: v }));

    if (v === SERVICE_PLAN_PAYMENT_PERIOD.MONTHLY) {
      dispatch(updateSingleField({ field: 'serviceSelection.additionalYears', value: 0 }));
    }
  };
  const handleAdditionalYearsChange = (v) => dispatch(updateSingleField({ field: 'serviceSelection.additionalYears', value: parseInt(v, 10) }));

  if (!selectedServicePlan) return null;

  return (
    <div>
      <form>
        {/* Plan Field */}
        <div className="row mb-3">
          <div className="col-sm-3 col-md-2">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="form-label font-weight-bold">Plan</label>
          </div>
          <div className="col-sm-9 col-md-10">
            {billMeReadOnly ? (
              <div>{servicePlanName}</div>
            ) : (
              <div className="mb-2">
                {servicePlanName}
                {showChangePlanLink && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ml-2"
                    onClick={onChangePlanClick}
                  >
                    &laquo; Choose a different plan
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bill Me Field */}
        <div className="row mb-3">
          <div className="col-sm-3 col-md-2">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="form-label font-weight-bold">Bill Me</label>
          </div>
          <div className="col-sm-9 col-md-10">
            {billMeReadOnly ? (
              <div>
                {selectedBillingCycle === SERVICE_PLAN_PAYMENT_PERIOD.MONTHLY
                  ? `Monthly ($${monthlyPrice}/mo)`
                  : `Yearly ($${(displayYearlyPrice / 12).toFixed(2)}/mo billed annually at $${displayYearlyPrice.toFixed(2)})`}
              </div>
            ) : (
              <div>
                {monthlyAvailable && (
                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      id="monthly-option"
                      name="billing-cycle"
                      value="monthly"
                      checked={selectedBillingCycle === SERVICE_PLAN_PAYMENT_PERIOD.MONTHLY}
                      onChange={(e) => handleBillingCycleChange(e.target.value)}
                      className="form-check-input"
                    />
                    <label htmlFor="monthly-option" className="form-check-label">
                      Monthly
                      {' '}
                      ($
                      {monthlyPrice}
                      /mo)
                    </label>
                  </div>
                )}

                <div className="form-check mb-2">
                  <input
                    type="radio"
                    id="yearly-option"
                    name="billing-cycle"
                    value="yearly"
                    checked={selectedBillingCycle === 'yearly'}
                    onChange={(e) => handleBillingCycleChange(e.target.value)}
                    className="form-check-input"
                  />
                  <label htmlFor="yearly-option" className="form-check-label">
                    Yearly
                    {' '}
                    ($
                    {(displayYearlyPrice / 12).toFixed(2)}
                    /mo billed annually at $
                    {displayYearlyPrice.toFixed(2)}
                    )
                    {hasYearlyDiscount && (
                      <span className="text-success ml-2">
                        {`Save ${discountPct}% your first year`}
                      </span>
                    )}
                  </label>
                </div>

                {hasYearlyDiscount && isYearlySelected && (
                  <div className="small text-muted mt-2">
                    {`*${discountPct}% discount applies to your first year only. Renews at $${yearly.toFixed(2)}/year.`}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Additional Years Field */}
        {selectedBillingCycle === SERVICE_PLAN_PAYMENT_PERIOD.YEARLY && (
          <div className="row mb-3">
            <div className="col-sm-3 col-md-2">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="additional-years" className="form-label font-weight-bold">
                Additional Years
              </label>
            </div>
            <div className="col-sm-9 col-md-10">
              <select
                id="additional-years"
                className="form-select form-control w-auto"
                value={selectedAdditionalYears}
                onChange={(e) => handleAdditionalYearsChange(e.target.value)}
              >
                {additionalYearsOptions.map((year) => (
                  <option key={year} value={year}>
                    {formatAdditionalYearsLabel(year)}
                  </option>
                ))}
              </select>
              {hasYearlyDiscount && selectedAdditionalYears > 0 && (
                <div className="small text-muted mt-2">
                  {`Additional years are billed at the full price of $${yearly.toFixed(2)}/year.`}
                </div>
              )}
            </div>
          </div>
        )}
      </form>

      <hr />
      <ServiceAddressForm
        serviceAddress={(contactInfo && contactInfo.serviceAddress) || null}
        onEditClick={() => setShowAddressModal(true)}
      />
      <ServiceAddressModal
        show={showAddressModal}
        handleClose={() => setShowAddressModal(false)}
        countryList={countryList}
        serviceAddress={(contactInfo && contactInfo.serviceAddress) || null}
      />
    </div>
  );
};

ServicePlanSelectionForm.propTypes = {
  onChangePlanClick: PropTypes.func.isRequired,
  countryList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    crmId: PropTypes.number,
    code: PropTypes.number,
    name: PropTypes.string,
    abbreviation: PropTypes.string,
  })),
};

ServicePlanSelectionForm.defaultProps = {
  countryList: [],
};

export default ServicePlanSelectionForm;
